"use client"

import { useState } from "react"

import { Button } from "@/components/ui/Button"

import OrderController from "../../app/controllers/order/order.controller"

import underlineIcon from "../../assets/images/order/underline.png"

import { FeatureSectionTitle } from "./components/FeatureSectionTitle"
import { FeaturesTable } from "./components/FeaturesTable"
import { Header } from "./components/Header"
import { HelpBanner } from "./components/HelpBanner"
import { PricingCard } from "./components/PricingCard"
import { SchoolCounter } from "./components/SchoolCounter"

export interface PricingPageProps {
  controller: OrderController
}

export function PricingPage({ controller }: PricingPageProps) {
  type PricingTab = "teacher" | "school"

  const [pricingTab, setPricingTab] = useState<PricingTab>(
    controller.viewingTeacherTab ? "teacher" : "school",
  )

  const [schoolsInDistrict, setSchoolsInDistrict] = useState(2)

  const [teamButtonVariant, setTeamButtonVariant] = useState<
    "default" | "outline" | "secondary" | "destructive" | "ghost"
  >("default")

  const goToTeacherTab = () => {
    setPricingTab("teacher")
  }

  const goToSchoolTab = () => {
    setPricingTab("school")
  }

  const handleTeamButtonClick = () => {
    setTeamButtonVariant("ghost")

    if (controller.isLoggedIn) {
      controller.showTeamPlanInterest()
    } else {
      controller.openEmailClientTeamPlan()
    }
  }

  const handleSchoolsInDistrictChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseInt(e.target.value)
    if (!isNaN(value) && value >= 2) {
      controller.schoolsInDistrict = value
      setSchoolsInDistrict(controller.schoolsInDistrict)
    }
  }

  const handleIncrementSchoolsInDistrict = () => {
    controller.incrementSchoolsInDistrict()
    setSchoolsInDistrict(controller.schoolsInDistrict)
  }

  const handleDecrementSchoolsInDistrict = () => {
    controller.decrementSchoolsInDistrict()
    setSchoolsInDistrict(controller.schoolsInDistrict)
  }

  const handleStartDistrictOrder = () => {
    controller.startDistrictOrder()
  }

  // Legacy Refactor: The height calculation on the topmost element accounts for:
  //   - Zoom of 1.6 on .react-container in src/index.css
  return (
    <div className="tw-h-[calc(100vh/1.6)] tw-overflow-auto tw-font-sansAlt">
      <Header
        isLoggedIn={controller.isLoggedIn}
        goToHomepage={() => controller.goToHomepage()}
        goToTeacherHelpCenter={() => controller.goToTeacherHelpCenter()}
        goToPlan={() => controller.goToPlan()}
        goToLogin={() => controller.goToLogin()}
        goToProfile={() => controller.goToProfile()}
      />

      {/* Body Content */}
      <div>
        <div className="tw-max-w-screen-xl tw-mx-auto tw-my-16 tw-px-5">
          <div className="tw-font-extrabold tw-text-center tw-mb-8 tw-text-[27px] tw-text-black tw-relative">
            Options for all classrooms to
            <span className="tw-relative tw-inline-block">
              &nbsp;help students grow
              <img
                className="tw-absolute tw-left-0 tw-bottom-[-5px] tw-w-[250px]"
                src={underlineIcon}
                alt="Underline"
              />
            </span>
          </div>

          {/* Toggle pill */}
          <div className="tw-relative tw-max-w-[190px] tw-h-[30px] tw-mt-15 tw-mb-8 tw-border-gray-200 tw-border-2 tw-flex tw-bg-white tw-rounded-full tw-mx-auto tw-p-1 tw-overflow-hidden">
            <div
              className={`tw-absolute tw-top-1 tw-bottom-1 tw-w-[calc(50%-2px)] tw-bg-green-100 tw-rounded-full tw-transition-transform tw-duration-300 tw-ease-in-out ${
                pricingTab === "teacher"
                  ? "tw-translate-x-0"
                  : "tw-translate-x-[calc(100%+4px)]"
              }`}
              aria-hidden="true"
            />

            <div className="tw-relative tw-z-10 tw-flex tw-w-full">
              <button
                className={`toggle-btn tw-flex-1 tw-px-3 tw-py-1.5 tw-text-sm tw-transition-colors tw-duration-300 ${
                  pricingTab === "teacher"
                    ? "tw-text-green-100 active"
                    : "tw-text-gray-500 tw-font-black"
                }`}
                onClick={goToTeacherTab}
              >
                Teachers
              </button>

              <button
                className={`toggle-btn tw-flex-1 tw-px-3 tw-py-1.5 tw-text-sm tw-transition-colors tw-duration-300 ${
                  pricingTab === "school"
                    ? "tw-text-green-900 active"
                    : "tw-text-gray-500 tw-font-black"
                }`}
                onClick={goToSchoolTab}
              >
                Schools / Districts
              </button>
            </div>
          </div>

          {/* Teacher Plans */}
          {pricingTab === "teacher" && (
            <div className="tw-flex tw-flex-wrap tw-gap-5 tw-justify-center tw-mb-10">
              {/* Basic Plan */}
              <PricingCard
                title="Basic"
                price="$ 0"
                description="I'm trying out practice and feedback for students"
                features={["10 Assignment Slots"]}
                buttonText="Sign up for free"
                buttonVariant="outline"
                onButtonClick={() => controller.goToSignup()}
              />

              {/* Starter Plan */}
              <PricingCard
                title="Starter"
                price={
                  <span>
                    $ <s>9.99</s> 4.99
                  </span>
                }
                period="/mo"
                description="Get 50% off for 4 months if you buy by May 31, 2025. Then $9.99/mo"
                features={[
                  "Individual License",
                  "75 Assignment Slots",
                  "Portfolio Student Accounts",
                ]}
                buttonText="Purchase (Monthly Only)"
                onButtonClick={() => controller.goToProTeacherPayment()}
              />

              {/* Team Plan */}
              <PricingCard
                title="Team"
                price="$ 20.99"
                period="/mo/teacher"
                description="Perfect for departments, eg: Math, ELA, Science"
                features={[
                  "Minimum of 4 User Licenses",
                  "Unlimited Additional Users",
                  "200 Assignment Slots/teacher",
                  "Team Collaboration",
                ]}
                buttonText={
                  controller.isLoggedIn
                    ? "I am Interested!"
                    : "Coming Soon (Contact Us)"
                }
                onButtonClick={handleTeamButtonClick}
                buttonVariant={teamButtonVariant}
              />
            </div>
          )}

          {/* School/District Plans */}
          {pricingTab === "school" && (
            <div className="tw-flex tw-flex-wrap tw-gap-5 tw-justify-center tw-mb-10">
              {/* Pro School Plan */}
              <PricingCard
                title="Pro School"
                price={
                  <span className="tw-text-2xl tw-font-black tw-mr-0.5">
                    Starts at $ <span className="tw-text-3xl">2,999</span>
                  </span>
                }
                period=" /year"
                description={
                  <span>
                    Get a 33% Discount on <s>$2,999</s> for <b>$1,999</b> if you
                    purchase before May 31
                  </span>
                }
                features={[
                  "Up to 100,000 student work units/school",
                  "Included Professional Development (PD) Sessions",
                  "School Wide Collaboration",
                ]}
                buttonText="View Instant School Quote"
                onButtonClick={() => controller.startSchoolOrder()}
              />

              {/* Pro District Plan */}
              <PricingCard
                title="Pro District"
                description="We're ready to monitor data, drive mastery and learning outcomes, and increase curriculum fidelity by 10x"
                features={[
                  "Unlimited Assignment Slots",
                  "Admin Controls",
                  "Google and Clever Rostering",
                  "District Curriculum Sharing",
                ]}
                buttonText="View Instant District Quote"
                onButtonClick={handleStartDistrictOrder}
                schoolCounterContent={
                  <SchoolCounter
                    value={schoolsInDistrict}
                    onChange={handleSchoolsInDistrictChange}
                    onIncrement={handleIncrementSchoolsInDistrict}
                    onDecrement={handleDecrementSchoolsInDistrict}
                    minValue={2}
                  />
                }
              />
            </div>
          )}

          <HelpBanner
            title="Interested in bringing Classkick to your school or district, but aren't sure where to start?"
            description="We'll help you draft an email to your administrators that includes a simple quote"
            buttonText="Share Quote with Admin"
            onButtonClick={() => controller.shareSchoolOrder()}
          />
        </div>

        {/* Compare Features Section - Teacher Plans */}
        {pricingTab === "teacher" && (
          <div className="tw-max-w-screen-xl tw-mx-auto tw-my-20 tw-px-5">
            <FeatureSectionTitle
              withRays={true}
              title={"Compare All Features"}
            />

            <FeaturesTable
              columns={[
                {
                  title: "Basic",
                  description:
                    "I'm trying out more practice and feedback for students",
                },
                {
                  title: "Starter",
                  description:
                    "Whoa this is cool, I'm going to try this more often",
                },
                {
                  title: "Team",
                  description:
                    "Perfect for departments, eg: Math, ELA, Science",
                },
              ]}
              features={[
                {
                  name: "Assignment Slots/Teacher",
                  values: ["10", "75", "200"],
                },
                {
                  name: "Student Work Units/Teacher",
                  values: ["1,000", "3,000", "-"],
                },
                {
                  name: "Student Work Units/School",
                  values: ["1,000", "5,000", "12,000"],
                },
                {
                  name: "Professional Development (PD) Sessions",
                  values: ["0", "3", "10"],
                },
                {
                  name: "Grades Synced",
                  values: ["0", "500", "2000"],
                },
                {
                  name: "AI Helper Sessions",
                  values: ["0", "250", "1000"],
                },
                {
                  name: "Student Portfolio Accounts",
                  values: ["0", "Unlimited", "Unlimited"],
                },
              ]}
              ctaButtons={[
                {
                  text: "Sign up for free",
                  variant: "outline",
                  onClick: () => controller.goToSignup(),
                },
                {
                  text: "Purchase (Monthly Only)",
                  variant: "default",
                  onClick: () => controller.goToProTeacherPayment(),
                },
                {
                  text: controller.isLoggedIn
                    ? "I am Interested!"
                    : "Coming Soon (Contact Us)",
                  variant: teamButtonVariant,
                  onClick: handleTeamButtonClick,
                },
              ]}
            />
          </div>
        )}

        {/* Compare Features Section - School/District Plans */}
        {pricingTab === "school" && (
          <div className="tw-max-w-screen-xl tw-mx-auto tw-my-20 tw-px-5">
            <FeatureSectionTitle
              withRays={true}
              title={"Compare All Features"}
            />

            <FeaturesTable
              columns={[
                {
                  title: "Pro School",
                  description:
                    "We are ready for the teachers in our school to drive mastery and learning outcomes",
                },
                {
                  title: "Pro District",
                  description:
                    "We are ready for all the schools in our district to drive mastery and learning outcomes",
                },
              ]}
              features={[
                {
                  name: "Student Work Units/School",
                  values: ["Up to 100,000", "Up to 250,000"],
                },
                {
                  name: "Grades synced",
                  values: ["10,000", "Unlimited"],
                },
                {
                  name: "AI Helper Sessions",
                  values: ["50,000", "Unlimited"],
                },
                {
                  name: "Assignment Slots/Teacher",
                  values: ["Unlimited", "Unlimited"],
                },
                {
                  name: "Professional Development (PD) Sessions",
                  values: ["Unlimited", "Unlimited"],
                },
                {
                  name: "Student Portfolio Accounts",
                  values: ["Unlimited", "Unlimited"],
                },
              ]}
              ctaButtons={[
                {
                  text: "Get an Early Bird Discount",
                  variant: "default",
                  onClick: () => controller.startSchoolOrder(),
                },
                {
                  text: "View District Quote",
                  variant: "default",
                  onClick: () => controller.startDistrictOrder(),
                },
              ]}
            />
          </div>
        )}

        {/* FAQ Section */}
        <div className="tw-text-center tw-my-8 tw-mb-24 tw-px-2.5">
          <span className="tw-text-black tw-font-black tw-text-base">
            Questions?
          </span>
          <span className="tw-text-light tw-text-base">
            &nbsp;Check out our
          </span>
          <Button
            className="tw-pl-1 tw-pr-0 tw-font-bold tw-text-base"
            variant="link"
            onClick={() => controller.openPricingFAQs()}
          >
            Pricing FAQs
          </Button>
          <span className="tw-text-light tw-text-base">
            &nbsp;or shoot us an email at pro@classkick.com
          </span>
        </div>
      </div>
    </div>
  )
}
